import { initContract } from '@ts-rest/core'
import { z } from 'zod'

const c = initContract()

const triggerEventEnum = z.enum([
	'new_member_registration',
	'membership_renewal',
	'check_in',
	'specific_datetime',
	'birthday',
	'birth_month_start',
	'recurring',
	'status_transition',
	'point_exchange'
])

const recurrenceTypeEnum = z.enum(['monthly', 'weekly', 'daily', 'end_of_month']).nullable().optional()

const campaignBody = z.object({
	name: z.string(),
	rewardType: z.enum(['none', 'points', 'coupon']),
	masterCouponId: z.number().nullable().optional(),
	rewardPoints: z.number().nullable().optional(),
	triggerEvent: triggerEventEnum,
	limitType: z.enum(['unlimited', 'limited']),
	usageLimit: z.number().nullable().optional(),
	hasActivePeriod: z.enum(['no', 'yes']),
	startDate: z.coerce.date().nullable().optional(),
	endDate: z.coerce.date().nullable().optional(),
	scheduledDate: z.coerce.date().nullable().optional(),
	scheduledTime: z.coerce.date().nullable().optional(),
	recurrenceType: recurrenceTypeEnum,
	recurrenceValue: z.string().nullable().optional(),
	recurrenceTime: z.coerce.date().nullable().optional(),
	status: z.enum(['active', 'inactive']),
	searchConditions: z.string().nullable().optional(),
	conditionDetails: z.string().nullable().optional()
})

const campaignUpdateBody = campaignBody.partial()

export type CampaignCreatePayload = z.infer<typeof campaignBody>
export type CampaignUpdatePayload = z.input<typeof campaignUpdateBody>
export type CampaignResponse = Omit<
	CampaignCreatePayload,
	'startDate' | 'endDate' | 'scheduledDate' | 'scheduledTime' | 'recurrenceTime'
> & {
	campaignId: number
	createdAt: string
	updatedAt: string
	startDate?: string | null
	endDate?: string | null
	scheduledDate?: string | null
	scheduledTime?: string | null
	recurrenceTime?: string | null
	couponMaster?: { couponMasterId: number; title: string } | null
}

export const CampaignAPI = c.router({
	listCampaigns: {
		method: 'GET',
		path: '/campaigns',
		summary: 'List all campaigns',
		query: z.object({
			page: z.coerce.number().optional().default(1),
			limit: z.coerce.number().optional().default(20),
			status: z.enum(['active', 'inactive']).optional(),
			triggerEvent: z.string().optional(),
			sortBy: z.enum(['campaignId', 'name', 'createdAt', 'status', 'triggerEvent']).optional().default('campaignId'),
			sortOrder: z.enum(['ASC', 'DESC']).optional().default('DESC')
		}),
		responses: {
			200: c.type<any>()
		}
	},
	getCampaign: {
		method: 'GET',
		path: '/campaigns/:campaignId',
		summary: 'Get a specific campaign',
		pathParams: z.object({
			campaignId: z.coerce.number()
		}),
		responses: {
			200: c.type<any>()
		}
	},
	createCampaign: {
		method: 'POST',
		path: '/campaigns',
		summary: 'Create a new campaign',
		body: campaignBody,
		responses: {
			200: c.type<any>()
		}
	},
	updateCampaign: {
		method: 'PUT',
		path: '/campaigns/:campaignId',
		summary: 'Update campaign properties',
		pathParams: z.object({
			campaignId: z.coerce.number()
		}),
		body: campaignUpdateBody,
		responses: {
			200: c.type<any>()
		}
	},
	deleteCampaign: {
		method: 'DELETE',
		path: '/campaigns/:campaignId',
		summary: 'Delete a campaign',
		pathParams: z.object({
			campaignId: z.coerce.number()
		}),
		body: z.object({}),
		responses: {
			200: c.type<any>()
		}
	},
	previewCampaign: {
		method: 'GET',
		path: '/campaigns/:campaignId/preview',
		summary: 'Preview a campaign audience calculation',
		pathParams: z.object({
			campaignId: z.coerce.number()
		}),
		responses: {
			200: c.type<any>()
		}
	},
	previewCampaignByCondition: {
		method: 'POST',
		path: '/campaigns/preview',
		summary: 'Preview a campaign audience calculation based on conditions',
		body: campaignBody.partial(),
		responses: {
			200: c.type<any>()
		}
	}
})
